import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const getProxyUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;
};

const getHeaders = () => {
  const sessionToken = localStorage.getItem('auth_session_token');
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken || '',
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };
};

export interface PackerBasePaint {
  product_naam: string;
  eenheid?: string;
  locatie: string;
  voorraad: number;
  match_type?: string;
  is_out_of_stock?: boolean;
  ignored_brand?: boolean;
  booking_product?: {
    product_id: string;
    product_naam: string | null;
    eenheid: string | null;
  } | null;
}

export interface PackerLineItem {
  lineitemindex: number;
  producttitle: string;
  sku?: string | null;
  volume?: string | null;
  color_code?: string | null;
  hex_color?: string | null;
  color_type?: "light" | "dark" | "medium" | null;
  collection_name?: string | null;
  quantity: number;
  image_url?: string | null;
  varianttitle?: string | null;
  base_paint?: PackerBasePaint | null;
  brand?: string | null;
}

export interface PackerOrder {
  ordernumber: string;
  orderid: number;
  status: string;
  is_printed: boolean;
  is_future_shipping?: boolean;
  customer: { city?: string };
  item_count: number;
  line_items: PackerLineItem[];
  shippingdate: string | null;
}

export interface PackersListMeta {
  total: number;
  limit: number;
  offset: number;
  packed_today: number;
  recently_cancelled: string[];
  recently_shipped: string[];
}

export interface PackersFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export const usePackersList = (filters: PackersFilters = {}) => {
  const sessionToken = typeof window !== 'undefined' ? localStorage.getItem('auth_session_token') : null;
  return useQuery({
    queryKey: ['packers-list', filters, sessionToken],
    queryFn: async () => {
      const params = new URLSearchParams({ action: 'packers_list' });
      if (filters.search) params.set('search', filters.search);
      params.set('limit', String(filters.limit || 100));
      if (filters.offset) params.set('offset', String(filters.offset));

      const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Fout bij ophalen packers lijst');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      const result = json as { success: boolean; data: PackerOrder[]; meta: PackersListMeta };

      // Enrich missing image_urls from local orders_live_ls
      try {
        const skusWithoutImage = new Set<string>();
        for (const order of result.data) {
          for (const item of order.line_items) {
            if (!item.image_url && item.sku) {
              skusWithoutImage.add(item.sku);
            }
          }
        }
        if (skusWithoutImage.size > 0) {
          // Try SKU-prefix matching: group by prefix (everything before last hyphen)
          const prefixToSkus = new Map<string, string[]>();
          for (const sku of skusWithoutImage) {
            const lastDash = sku.lastIndexOf("-");
            const prefix = lastDash > 0 ? sku.substring(0, lastDash) : sku;
            if (!prefixToSkus.has(prefix)) prefixToSkus.set(prefix, []);
            prefixToSkus.get(prefix)!.push(sku);
          }

          // Query orders_live_ls for any row with matching SKU prefix that has an image
          const allPrefixes = [...prefixToSkus.keys()];
          const imageMap = new Map<string, string>();

          // Query in chunks of 20 prefixes using ilike
          for (let i = 0; i < allPrefixes.length; i += 20) {
            const chunk = allPrefixes.slice(i, i + 20);
            // Use exact SKU match first, then prefix
            const { data: rows } = await supabase
              .from("orders_live_ls")
              .select("sku, image_url")
              .not("image_url", "is", null)
              .not("sku", "is", null)
              .in("sku", [...skusWithoutImage])
              .limit(500);

            if (rows) {
              for (const row of rows) {
                if (row.sku && row.image_url && !imageMap.has(row.sku)) {
                  imageMap.set(row.sku, row.image_url);
                }
              }
            }

            // Also try prefix-based matching for unresolved SKUs
            const unresolvedPrefixes = chunk.filter(p => {
              const skus = prefixToSkus.get(p) || [];
              return skus.some(s => !imageMap.has(s));
            });
            if (unresolvedPrefixes.length > 0) {
              // Build OR filter for prefix matching
              const orFilter = unresolvedPrefixes.map(p => `sku.like.${p}%`).join(",");
              const { data: prefixRows } = await supabase
                .from("orders_live_ls")
                .select("sku, image_url")
                .not("image_url", "is", null)
                .not("sku", "is", null)
                .or(orFilter)
                .limit(500);

              if (prefixRows) {
                for (const row of prefixRows) {
                  if (row.sku && row.image_url) {
                    const lastDash = row.sku.lastIndexOf("-");
                    const prefix = lastDash > 0 ? row.sku.substring(0, lastDash) : row.sku;
                    // Map all SKUs with same prefix to this image
                    const targetSkus = prefixToSkus.get(prefix);
                    if (targetSkus) {
                      for (const s of targetSkus) {
                        if (!imageMap.has(s)) {
                          imageMap.set(s, row.image_url);
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          // Apply images to line items
          if (imageMap.size > 0) {
            for (const order of result.data) {
              for (const item of order.line_items) {
                if (!item.image_url && item.sku && imageMap.has(item.sku)) {
                  item.image_url = imageMap.get(item.sku)!;
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn("[usePackersList] Image enrichment failed:", e);
      }

      return result;
    },
    enabled: !!sessionToken,
    refetchInterval: 30000,
  });
};

export const useMarkPrinted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_printed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij markeren als afgedrukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) gemarkeerd als afgedrukt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useMarkPacked = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_packed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij inpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) ingepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUnpack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'unpack', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij uitpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) uitgepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

// Decode base64 PDF and open in new tab
const downloadBase64Pdf = (base64: string, filename: string) => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Fetch a PDF label for a single order and open it in a new tab
export const fetchLabel = async (ordernumber: string): Promise<void> => {
  const params = new URLSearchParams({ action: 'generate_labels', ordernumber });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Fout bij ophalen label voor ${ordernumber}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `etiketten-${ordernumber}.pdf`);
};

// Fetch labels for multiple orders in a single request
export const fetchLabelsForOrders = async (ordernumbers: string[]): Promise<void> => {
  const params = new URLSearchParams({
    action: 'generate_labels',
    ordernumbers: ordernumbers.join(','),
  });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Fout bij ophalen labels');
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `etiketten-${ordernumbers.join('-')}.pdf`);
};

export const usePrintLabel = () => {
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      await fetchLabelsForOrders(ordernumbers);
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} label(s) gedownload`);
    },
    onError: (error: Error) => {
      if (error.message.includes('base paint') || error.message.includes('No items')) {
        toast.error('Labels niet beschikbaar: basisverf-matching ontbreekt voor deze order(s). Dit moet eerst in het kleurenorders-systeem worden verwerkt.', { duration: 6000 });
      } else {
        toast.error(error.message);
      }
    },
  });
};
